/**
 * Specialty slug resolution (rev v11.0.0 · Phase 2 / C2).
 *
 * THE PROBLEM
 * -----------
 * The platform carried two incompatible specialty vocabularies:
 *
 *   apps/web/src/lib/ngap-specialities.ts → 15 kebab-case slugs
 *   apps/web/src/lib/referential/ngap.ts   → 24 UPPERCASE codes
 *
 * `MED` and `MEDG` both meant general medicine. `OBST` meant obstetrics,
 * which the slug list carried inside `gynecologie`. `IMAG` meant imaging,
 * which the slug list called `radiologie`. Nothing in the codebase recorded
 * those equivalences, so every call site had to guess.
 *
 * THIS MODULE IS THE SINGLE MAPPING SOURCE.
 *
 * Resolution is case-insensitive and accepts either vocabulary.
 */
import { SPECIALTIES } from "./specialties";

/**
 * Build the alias index once at module load.
 * Keys are uppercased so lookup is case-insensitive.
 */
const ALIAS_INDEX: ReadonlyMap<string, string> = (() => {
  const index = new Map<string, string>();
  for (const specialty of SPECIALTIES) {
    index.set(specialty.id.toUpperCase(), specialty.id);
    for (const alias of specialty.aliases) {
      const key = alias.toUpperCase();
      const existing = index.get(key);
      if (existing && existing !== specialty.id) {
        // A duplicated alias is a data error, not a runtime surprise.
        throw new Error(
          `specialty alias '${alias}' maps to both '${existing}' and '${specialty.id}'`,
        );
      }
      index.set(key, specialty.id);
    }
  }
  return index;
})();

/**
 * Resolve any identifier — canonical slug or legacy code — to the canonical
 * slug. Returns null for an unknown identifier.
 */
export function resolveSpecialtyId(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const key = raw.trim().toUpperCase();
  return ALIAS_INDEX.get(key) ?? null;
}

/** True when the identifier is known to the registry. */
export function isKnownSpecialty(raw: string | null | undefined): boolean {
  return resolveSpecialtyId(raw) !== null;
}

/** Every identifier that resolves to the given slug, including the slug. */
export function specialtyAliases(slug: string): string[] {
  const canonical = resolveSpecialtyId(slug);
  if (!canonical) return [];
  const specialty = SPECIALTIES.find((s) => s.id === canonical);
  return specialty ? [specialty.id, ...specialty.aliases] : [];
}

/**
 * The legacy codes that resolve to a slug. Excludes the slug itself.
 * Used by the vocabulary-reconciliation report.
 */
export function legacyCodesFor(slug: string): string[] {
  const canonical = resolveSpecialtyId(slug);
  if (!canonical) return [];
  const specialty = SPECIALTIES.find((s) => s.id === canonical);
  return specialty ? [...specialty.aliases] : [];
}

/**
 * Reverse lookup: which canonical slugs does a legacy code resolve to?
 * A code must resolve to exactly one slug.
 */
export function slugsForLegacyCode(code: string): string[] {
  const canonical = resolveSpecialtyId(code);
  return canonical ? [canonical] : [];
}
